import { Suspense, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import ScrollMemory from 'react-router-scroll-memory';
import { buildFacebookConversionData } from 'utils/tracking';
import RebuildCart from 'components/rebuild_cart';
import { setStep } from 'redux/actions/booking';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import TripAdvisor from 'tenants/cccsyd/trip_advisor_conversion_pixel';
import BookWithConfidence from '../book_with_confidence';
import BookingSteps from '../booking_steps';
import CartSummary from '../cart_summary';
import CheckoutForm from '../checkout_form';
import ConfirmBooking from '../confirm_booking';
import EmptyCart from '../empty_cart';
import RelatedProducts from '../related_products';
import ReviewCart from '../review_cart';
import StepControl from '../step_control';

export const Review = () => {
  const location = useLocation();
  const warningMsg = location.state?.warning;
  const { relatedProductsTitle, relatedProductsText, hideRelatedProducts } =
    window.ecom.craftConfig.relatedProducts;
  const { title, text, link, linkText } = window.ecom.craftConfig.emptyCart;
  const bookingInfo = useSelector(getCurrentBooking);
  const hasCartData = bookingInfo.reservations_attributes?.length;
  return (
    <div data-testid="review-step">
      {hasCartData ? (
        <>
          <div className="row cart-section__header mb-4">
            <div className="col-12 col-lg-8">
              <h2>Review your Cart</h2>
              <div className="mt-2 text-x-small text-body-secondary">
                Your booking reference is: {bookingInfo.reference}
              </div>
            </div>
            <div className="col-12 col-lg-4">
              <BookingSteps />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8 divider-y-6 pb-5">
              <ReviewCart />
              <div className="text-end border-0 py-0 mt-4">
                <Link
                  className="d-block text-decoration-none"
                  to="/cart/checkout"
                  title="Continue to Checkout"
                >
                  Continue to Checkout{' '}
                  <i className="fas fa-arrow-right ms-1" aria-hidden="true" />
                </Link>
              </div>
              {!hideRelatedProducts && (
                <RelatedProducts
                  {...{
                    title: relatedProductsTitle,
                    text: relatedProductsText,
                  }}
                />
              )}
              <StepControl />
            </div>
            <div className="col-lg-4 space-y-4">
              <BookWithConfidence />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="row cart-section__header mb-4">
            <div className="col-12 col-lg-8">
              <h2>Your cart is empty</h2>
              {warningMsg && (
                <div className="mt-4 alert alert-warning" role="alert">
                  {warningMsg}
                </div>
              )}
            </div>
            <div className="col-12 col-lg-4">
              <BookingSteps />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8 divider-y-6 pb-5">
              <EmptyCart {...{ title, text, link, linkText }} />
              <RelatedProducts
                {...{ title: relatedProductsTitle, text: relatedProductsText }}
              />
            </div>
            <div className="col-lg-4 space-y-4">
              <BookWithConfidence />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const Checkout = () => {
  const bookingInfo = useSelector(getCurrentBooking);

  return (
    <div data-testid="checkout-step">
      <div className="row cart-section__header mb-4">
        <div className="col-12 col-lg-8">
          <div className="d-lg-none">
            <Link to="/cart" className="checkout__back">
              <i className="fas fa-chevron-left" /> Return to Cart Review
            </Link>
          </div>
          <h2>Let&rsquo;s finalise your booking</h2>
          <div className="mt-2 text-x-small text-body-secondary">
            Your booking reference is: {bookingInfo.reference}
          </div>
        </div>
        <div className="col-12 col-lg-4">
          <div className="mt-4 d-lg-none">
            <CartSummary />
          </div>
          <BookingSteps />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8 space-y-4">
          <CheckoutForm />
        </div>
        <div className="col-lg-4 space-y-4">
          <div className="d-none d-lg-block">
            <CartSummary />
          </div>
        </div>
      </div>
    </div>
  );
};

export const Confirm = () => {
  const bookingInfo = useSelector(getCurrentBooking);

  // Facebook pixel tracking - fire once on component mount
  // Note: In development, React StrictMode intentionally runs effects twice to detect side effects
  // This causes the pixel to fire twice in dev, but only once in production
  useEffect(() => {
    if (typeof fbq !== 'undefined' && fbq) {
      fbq('track', 'Purchase', buildFacebookConversionData(bookingInfo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = fires once on mount, intentionally ignoring bookingInfo changes

  return (
    <>
      <TripAdvisor />
      <div className="row" data-testid="confirm-step">
        <div className="col-lg-8 space-y-4">
          <ConfirmBooking />
          <StepControl />
        </div>
        <div className="col-lg-4 space-y-4">
          <BookingSteps />
          <div className="d-none d-lg-block">
            <CartSummary readOnly paid title="Order Confirmation" />
          </div>
        </div>
      </div>
    </>
  );
};

export function Cart() {
  const stepUrls = ['/cart', '/cart/checkout', '/cart/confirm'];
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setStep(stepUrls.indexOf(window.location.pathname) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Suspense fallback={<Skeleton height={800} />}>
      <ScrollMemory />
      <Switch>
        <Route path="/cart" exact component={Review} />
        <Route path="/cart/checkout" exact component={Checkout} />
        <Route path="/cart/confirm" exact component={Confirm} />
        <Route path="/cart/rebuild" exact component={RebuildCart} />
        <Route path="/cart" render={() => <Redirect to="/cart" />} />
      </Switch>
    </Suspense>
  );
}
